import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../lib/api";

const ACTION_STYLES = {
  "coach.approved":         "text-[#2A5BFF] border-[#2A5BFF]",
  "coach.suspended":        "text-yellow-400 border-yellow-500/60",
  "coach.promoted":         "text-white border-white/40",
  "coach.demoted":          "text-red-400 border-red-500/60",
  "order.assigned":         "text-[#2A5BFF] border-[#2A5BFF]",
  "order.reassigned":       "text-[#2A5BFF] border-[#2A5BFF]",
  "order.unassigned":       "text-white/60 border-white/20",
  "order.status_changed":   "text-white/80 border-white/30",
  "service.price_changed":  "text-yellow-300 border-yellow-500/60",
  "service.created":        "text-[#2A5BFF] border-[#2A5BFF]",
  "service.updated":        "text-white/70 border-white/25",
  "service.deleted":        "text-red-400 border-red-500/60",
};

function ActionPill({ action }) {
  const cls = ACTION_STYLES[action] || "text-white/50 border-white/15";
  return (
    <span className={`text-[10px] tracking-[0.22em] uppercase px-2 py-1 border ${cls}`}>
      {action}
    </span>
  );
}

function formatDiff(v) {
  if (v === null || v === undefined) return <span className="text-white/30">—</span>;
  if (typeof v !== "object") return <span>{String(v)}</span>;
  const entries = Object.entries(v);
  return (
    <div className="space-y-0.5">
      {entries.map(([k, val]) => (
        <div key={k} className="text-[11px]">
          <span className="text-white/40">{k}:</span>{" "}
          <span className="text-white/85">{val === null ? "null" : String(val)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Read-only admin audit log. Newest actions first. Optional action filter.
 * Uses the existing HTTP Basic admin auth pattern (same as /admin/orders).
 */
export default function AdminAuditLog() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");

  const authHeader = () => ({
    headers: { Authorization: "Basic " + btoa(`${username}:${password}`) },
  });

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit-log`, {
        ...authHeader(),
        params: { limit: 500 },
      });
      setRows(r.data);
      setAuthed(true);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid credentials." : "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const visible = filter === "all" ? rows : rows.filter((r) => r.action.startsWith(filter));

  return (
    <div className="min-h-screen bg-[#050510] text-white" data-testid="admin-audit-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-center gap-6 text-[11px] tracking-[0.3em] uppercase text-white/60 flex-wrap">
          <Link to="/" className="hover:text-[#2A5BFF]">← Back</Link>
          <Link to="/admin" className="hover:text-[#2A5BFF]">Waitlist</Link>
          <Link to="/admin/services" className="hover:text-[#2A5BFF]">Services</Link>
          <Link to="/admin/users" className="hover:text-[#2A5BFF]">Users</Link>
          <Link to="/admin/orders" className="hover:text-[#2A5BFF]">Orders</Link>
        </div>
        <h1 className="font-anton uppercase text-5xl md:text-6xl mt-8">Admin — Audit Log</h1>

        {!authed && (
          <form onSubmit={load} className="mt-10 max-w-md border border-white/10 p-6" data-testid="admin-audit-login">
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Username</label>
            <input
              className="input-line focus-ring"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              data-testid="admin-audit-username"
              required
            />
            <label className="mt-6 block text-[10px] tracking-[0.32em] uppercase text-white/50">Password</label>
            <input
              type="password"
              className="input-line focus-ring"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="admin-audit-password"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-primary mt-8 px-6 py-3 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60"
              data-testid="admin-audit-login-button"
            >
              {loading ? "…" : "Sign In"}
            </button>
          </form>
        )}

        {authed && (
          <div className="mt-10" data-testid="admin-audit-content">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="text-[11px] tracking-[0.28em] uppercase text-white/50">
                {visible.length} / {rows.length} events (newest first)
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {[["all", "All"], ["coach", "Coach"], ["order", "Order"], ["service", "Service"]].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setFilter(k)}
                    className={`px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border ${filter === k ? "border-[#2A5BFF] text-[#2A5BFF]" : "border-white/15 text-white/60 hover:text-white"}`}
                    data-testid={`admin-audit-filter-${k}`}
                  >
                    {label}
                  </button>
                ))}
                <button
                  onClick={load}
                  className="ml-2 px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border border-white/15 text-white/60 hover:text-white"
                  data-testid="admin-audit-refresh"
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.28em] uppercase text-white/40 border-b border-white/10">
                  <tr>
                    <th className="p-4 text-left">Time (UTC)</th>
                    <th className="p-4 text-left">Admin</th>
                    <th className="p-4 text-left">IP</th>
                    <th className="p-4 text-left">Action</th>
                    <th className="p-4 text-left">Entity</th>
                    <th className="p-4 text-left">Old</th>
                    <th className="p-4 text-left">New</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`admin-audit-row-${r.id}`}>
                      <td className="p-4 text-white/60 text-[11px] font-mono">
                        {new Date(r.timestamp).toISOString().replace("T", " ").slice(0, 19)}
                      </td>
                      <td className="p-4 text-white/80">{r.admin_user}</td>
                      <td className="p-4 text-white/50 text-[11px] font-mono">{r.ip || "—"}</td>
                      <td className="p-4"><ActionPill action={r.action} /></td>
                      <td className="p-4">
                        <div className="text-[11px] text-white/45 uppercase tracking-[0.18em]">{r.entity_type}</div>
                        <div className="font-mono text-[11px] text-white/80">{r.entity_id.slice(0, 8)}…</div>
                      </td>
                      <td className="p-4">{formatDiff(r.old_value)}</td>
                      <td className="p-4">{formatDiff(r.new_value)}</td>
                    </tr>
                  ))}
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-white/40 text-sm">No events match this filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
